import React, { useState } from "react";
import { FormRenderProps } from "@progress/kendo-react-form";
import { FaWhatsapp } from "react-icons/fa";
import {
  FiMinus,
  FiMaximize2,
  FiMinimize2,
  FiX,
  FiExternalLink,
  FiPhone,
  FiCornerDownLeft,
  FiSend,
  FiImage,
} from "react-icons/fi";
import IconComponent from "../../../components/common/IconComponent";
import moment from "moment";

const WhatsappIcon = FaWhatsapp as any;
const MinusIcon = FiMinus as any;
const MaximizeIcon = FiMaximize2 as any;
const MinimizeIcon = FiMinimize2 as any;
const CloseIcon = FiX as any;
const ExternalLinkIcon = FiExternalLink as any;
const PhoneIcon = FiPhone as any;
const ReplyIcon = FiCornerDownLeft as any;
const SendIcon = FiSend as any;
const ImageIcon = FiImage as any;

interface WhatsappLivePreviewProps {
  selectedApiTemplate: any;
  parsedApiButtons: any[];
  selectedCount: number;
  formRenderProps: FormRenderProps;
  renderPreviewWithVariables: (
    text: string,
    formRenderProps: FormRenderProps,
  ) => string;
  onSendClick?: () => void;
  sending?: boolean;
  isSendAccess?: boolean;
  isFloating?: boolean;
  title?: string;
  subtitle?: string;
}

export const WhatsappLivePreview: React.FC<WhatsappLivePreviewProps> = ({
  selectedApiTemplate,
  parsedApiButtons,
  selectedCount,
  formRenderProps,
  renderPreviewWithVariables,
  onSendClick,
  sending = false,
  isSendAccess = true,
  isFloating = true,
  title = "Kavish Hotels",
  subtitle = "online",
}) => {
  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const [isMaximized, setIsMaximized] = useState<boolean>(false);
  const [isClosed, setIsClosed] = useState<boolean>(false);

  const previewBody = selectedApiTemplate?.body
    ? renderPreviewWithVariables(selectedApiTemplate.body, formRenderProps)
    : "";

  const previewHeader = selectedApiTemplate?.header
    ? renderPreviewWithVariables(selectedApiTemplate.header, formRenderProps)
    : "";

  const previewFooter = selectedApiTemplate?.footer
    ? renderPreviewWithVariables(selectedApiTemplate.footer, formRenderProps)
    : "";

  const isImageUrlString = (urlStr: any): boolean => {
    if (typeof urlStr !== "string") return false;
    const clean = urlStr.trim().toLowerCase();
    return (
      clean.startsWith("http://") ||
      clean.startsWith("https://") ||
      clean.startsWith("data:image/") ||
      clean.startsWith("blob:") ||
      /\.(jpeg|jpg|gif|png|webp|svg)(\?.*)?$/i.test(clean)
    );
  };

  const getHeaderImageUrl = (): string | null => {
    if (!selectedApiTemplate) return null;

    const fileUrl = selectedApiTemplate.header_handle_file_url;
    if (isImageUrlString(fileUrl)) return fileUrl.trim();

    const headerVal = selectedApiTemplate.header;
    if (isImageUrlString(headerVal)) return headerVal.trim();

    if (isImageUrlString(previewHeader)) return previewHeader.trim();

    return null;
  };

  const headerImageUrl = getHeaderImageUrl();

  const isImageHeaderType = (): boolean => {
    if (headerImageUrl) return true;
    if (!selectedApiTemplate) return false;
    const format = String(selectedApiTemplate.header_format || "").toUpperCase();
    return format === "IMAGE";
  };

  const renderHeader = (showWindowControls: boolean = false) => (
    <div
      style={{
        backgroundColor: "#006653",
        color: "#ffffff",
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        userSelect: "none",
        cursor: showWindowControls ? "pointer" : "default",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        height: "52px",
        boxSizing: "border-box",
      }}
      onClick={showWindowControls ? () => setIsMinimized(!isMinimized) : undefined}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          minWidth: 0,
          flex: 1,
        }}
      >
        <div
          style={{
            width: "34px",
            height: "34px",
            borderRadius: "50%",
            backgroundColor: "#25d366",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ffffff",
            flexShrink: 0,
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          }}
        >
          <IconComponent icon={WhatsappIcon} style={{ fontSize: "20px" }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: "14px",
              color: "#ffffff",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: "11.5px",
              color: "#b5eae0",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {selectedApiTemplate
              ? `Preview: ${selectedApiTemplate.display_name || selectedApiTemplate.name}`
              : subtitle}
          </div>
        </div>
      </div>

      {showWindowControls && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginLeft: "12px",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Minimize */}
          <button
            type="button"
            onClick={() => setIsMinimized(!isMinimized)}
            style={{
              background: "transparent",
              border: "none",
              color: "#e2e8f0",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
            }}
            title={isMinimized ? "Expand" : "Minimize"}
          >
            <IconComponent icon={MinusIcon} style={{ fontSize: "16px" }} />
          </button>

          {/* Maximize / Restore */}
          <button
            type="button"
            onClick={() => {
              if (isMinimized) setIsMinimized(false);
              setIsMaximized(!isMaximized);
            }}
            style={{
              background: "transparent",
              border: "none",
              color: "#e2e8f0",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
            }}
            title={isMaximized ? "Restore" : "Maximize"}
          >
            <IconComponent
              icon={isMaximized ? MinimizeIcon : MaximizeIcon}
              style={{ fontSize: "14px" }}
            />
          </button>

          {/* Close */}
          <button
            type="button"
            onClick={() => setIsClosed(true)}
            style={{
              background: "transparent",
              border: "none",
              color: "#e2e8f0",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
            }}
            title="Close"
          >
            <IconComponent icon={CloseIcon} style={{ fontSize: "16px" }} />
          </button>
        </div>
      )}
    </div>
  );

  const renderContent = (showWindowControls: boolean = false) => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#efeae2",
        backgroundImage:
          "radial-gradient(#b1a798 1.2px, transparent 1.2px)",
        backgroundSize: "16px 16px",
      }}
    >
      {/* Unified WhatsApp Header */}
      {renderHeader(showWindowControls)}

      {/* Chat Messages Body */}
      <div
        style={{
          flex: "1 1 0%",
          minHeight: 0,
          padding: "10px 16px",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        {/* Date Pill Divider */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            margin: "4px 0 2px 0",
          }}
        >
          <span
            style={{
              backgroundColor: "#e1f3fb",
              color: "#417793",
              fontSize: "11px",
              fontWeight: 600,
              padding: "4px 14px",
              borderRadius: "8px",
              textTransform: "uppercase",
              letterSpacing: "0.03em",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            }}
          >
            TODAY
          </span>
        </div>

        {selectedApiTemplate ? (
          <div
            style={{
              maxWidth: "88%",
              alignSelf: "flex-end",
              backgroundColor: "#dcf8c6",
              borderRadius: "12px 12px 0px 12px",
              boxShadow: "0 1px 3px rgba(11, 20, 26, 0.12)",
              padding: "10px 12px",
              position: "relative",
              border: "1px solid rgba(0, 0, 0, 0.04)",
            }}
          >
            {/* Header Image or Header Text */}
            {headerImageUrl ? (
              <div
                style={{
                  marginTop: "-10px",
                  marginLeft: "-12px",
                  marginRight: "-12px",
                  marginBottom: "8px",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                  overflow: "hidden",
                  backgroundColor: "#e2e8f0",
                }}
              >
                <img
                  src={headerImageUrl}
                  alt="Template Header"
                  style={{
                    width: "100%",
                    maxHeight: "220px",
                    objectFit: "cover",
                    display: "block",
                  }}
                  onError={(e: any) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            ) : isImageHeaderType() ? (
              <div
                style={{
                  marginTop: "-10px",
                  marginLeft: "-12px",
                  marginRight: "-12px",
                  marginBottom: "8px",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                  backgroundColor: "#cbd5e1",
                  height: "140px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#475569",
                  gap: "6px",
                }}
              >
                <IconComponent icon={ImageIcon} style={{ fontSize: "32px", color: "#64748b" }} />
                <span style={{ fontSize: "12px", fontWeight: 600 }}>[ Image Header ]</span>
              </div>
            ) : previewHeader && !isImageUrlString(previewHeader) ? (
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "13.5px",
                  color: "#111b21",
                  marginBottom: "6px",
                  borderBottom: "1px dashed #e9edef",
                  paddingBottom: "4px",
                }}
              >
                {previewHeader}
              </div>
            ) : null}

            {/* Template Body Text */}
            <div
              style={{
                fontSize: "13px",
                color: "#111b21",
                lineHeight: "1.5",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {previewBody}
            </div>

            {/* Template Footer */}
            {previewFooter && (
              <div
                style={{
                  fontSize: "11px",
                  color: "#667781",
                  marginTop: "6px",
                  fontStyle: "italic",
                }}
              >
                {previewFooter}
              </div>
            )}

            {/* Message Timestamp & Double Blue Ticks */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "4px",
                marginTop: "4px",
                fontSize: "10px",
                color: "#667781",
              }}
            >
              <span>{moment().format("h:mm A")}</span>
              <span
                style={{
                  color: "#34b7f1",
                  fontWeight: 700,
                  fontSize: "12px",
                  lineHeight: 1,
                }}
              >
                ✓✓
              </span>
            </div>

            {/* Template Action Buttons (Original WhatsApp Chat Style) */}
            {parsedApiButtons.length > 0 && (
              <div
                style={{
                  marginTop: "8px",
                  marginLeft: "-12px",
                  marginRight: "-12px",
                  marginBottom: "-10px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {parsedApiButtons.map((btn: any, idx: number) => {
                  const type = btn.type || btn.button_type;
                  const text = btn.text || btn.title || `Button ${idx + 1}`;
                  return (
                    <div
                      key={idx}
                      style={{
                        borderTop: "1px solid rgba(0, 0, 0, 0.08)",
                        padding: "10px 12px",
                        color: "#008069",
                        fontSize: "13px",
                        fontWeight: 600,
                        textAlign: "center",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                        cursor: "pointer",
                      }}
                    >
                      {type === "URL" && (
                        <IconComponent icon={ExternalLinkIcon} style={{ fontSize: "14px", color: "#008069" }} />
                      )}
                      {type === "PHONE_NUMBER" && (
                        <IconComponent icon={PhoneIcon} style={{ fontSize: "14px", color: "#008069" }} />
                      )}
                      {type === "QUICK_REPLY" && (
                        <IconComponent icon={ReplyIcon} style={{ fontSize: "14px", color: "#008069" }} />
                      )}
                      <span>{text}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              padding: "20px 10px",
            }}
          >
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                padding: "22px 24px",
                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                border: "1px solid #e9edef",
                width: "90%",
                maxWidth: "380px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  color: "#667781",
                  fontSize: "13.5px",
                  lineHeight: "1.5",
                  fontWeight: 400,
                }}
              >
                Select a WhatsApp Template to view preview message here.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Preview Footer Action Bar */}
      <div
        style={{
          backgroundColor: "#efeae2",
          padding: "10px 14px",
          borderTop: "1px solid rgba(0, 0, 0, 0.05)",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        {/* White Rounded Input Pill */}
        <div
          style={{
            flex: 1,
            backgroundColor: "#ffffff",
            borderRadius: "24px",
            padding: "10px 18px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
            border: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
          }}
        >
          <span
            style={{
              color: "#64748b",
              fontSize: "13.5px",
              userSelect: "none",
            }}
          >
            Send WhatsApp ({selectedCount})
          </span>
        </div>

        {/* Circular Send Button */}
        <button
          type="button"
          onClick={onSendClick}
          disabled={
            sending ||
            !selectedApiTemplate ||
            selectedCount === 0 ||
            !isSendAccess
          }
          style={{
            width: "42px",
            height: "42px",
            borderRadius: "50%",
            backgroundColor:
              sending ||
              !selectedApiTemplate ||
              selectedCount === 0 ||
              !isSendAccess
                ? "#94a3b8"
                : "#00a884",
            color: "#ffffff",
            border: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor:
              sending ||
              !selectedApiTemplate ||
              selectedCount === 0 ||
              !isSendAccess
                ? "not-allowed"
                : "pointer",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            transition: "all 0.2s ease",
            flexShrink: 0,
          }}
          title="Send WhatsApp Message"
        >
          <IconComponent
            icon={SendIcon}
            style={{ fontSize: "16px", transform: "translateX(1px)" }}
          />
        </button>
      </div>
    </div>
  );

  if (isFloating) {
    if (isClosed) {
      return (
        <button
          type="button"
          onClick={() => {
            setIsClosed(false);
            setIsMinimized(false);
          }}
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            zIndex: 9999,
            backgroundColor: "#128c7e",
            color: "#ffffff",
            borderRadius: "30px",
            padding: "10px 18px",
            boxShadow: "0 10px 25px rgba(18, 140, 126, 0.4)",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            fontWeight: 700,
            fontSize: "13px",
            transition: "all 0.2s ease",
          }}
        >
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <IconComponent icon={WhatsappIcon} style={{ fontSize: "20px", color: "#ffffff" }} />
            {selectedApiTemplate && (
              <span
                style={{
                  position: "absolute",
                  top: "-4px",
                  right: "-4px",
                  width: "10px",
                  height: "10px",
                  backgroundColor: "#25d366",
                  borderRadius: "50%",
                  border: "2px solid #128c7e",
                }}
              />
            )}
          </div>
          <span>WhatsApp Preview</span>
          {selectedCount > 0 && (
            <span
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.25)",
                padding: "2px 8px",
                borderRadius: "12px",
                fontSize: "11px",
              }}
            >
              {selectedCount}
            </span>
          )}
        </button>
      );
    }

    // Floating Window anchored at Bottom-Right
    return (
      <div
        style={{
          position: "fixed",
          bottom: isMinimized ? "0px" : isMaximized ? "50%" : "0px",
          right: isMinimized ? "24px" : isMaximized ? "50%" : "24px",
          transform: !isMinimized && isMaximized ? "translate(50%, 50%)" : "none",
          width: isMinimized ? "380px" : isMaximized ? "850px" : "460px",
          maxWidth: "92vw",
          height: isMinimized ? "52px" : isMaximized ? "85vh" : "540px",
          maxHeight: isMinimized ? "52px" : isMaximized ? "85vh" : "80vh",
          zIndex: 9999,
          borderRadius: isMinimized ? "10px 10px 0 0" : isMaximized ? "16px" : "12px 12px 0 0",
          backgroundColor: "#ffffff",
          boxShadow: "0 20px 50px -10px rgba(15, 23, 42, 0.4)",
          border: "1px solid #cbd5e1",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          transition: "all 0.25s ease-out",
        }}
      >
        {isMinimized ? (
          renderHeader(true)
        ) : (
          renderContent(true)
        )}
      </div>
    );
  }

  // Embedded view fallback
  return (
    <div
      style={{
        borderRadius: "16px",
        overflow: "hidden",
        border: "1px solid #cbd5e1",
        boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
        height: "550px",
      }}
    >
      {renderContent(false)}
    </div>
  );
};
